/**
 * Custom hook for managing venture filter state
 * Provides state management for category and status filters
 *
 * Requirements:
 * - 9.3: Filter by category in public discovery
 * - 11.3: Filter by status in workspace ventures
 * - 12.3: Category validation and filtering
 */
import { useState, useCallback, useMemo } from "react";
/**
 * Hook for managing venture filter state
 * Provides a clean interface for filtering ventures by category and status
 *
 * @param initialFilters - Optional initial filter values
 * @returns Filter state and actions
 *
 * @example
 * ```tsx
 * const {
 *   filters,
 *   setStatusFilter,
 *   setTypeFilter,
 *   clearFilters,
 *   hasActiveFilters
 * } = useVentureFilters({ status: 'live' });
 *
 * // Use filters with data fetching hook
 * const { ventures } = usePublicVentures(filters);
 *
 * // Update filters
 * setTypeFilter('real_estate');
 * setStatusFilter('successful');
 *
 * // Clear all filters
 * if (hasActiveFilters) {
 *   clearFilters();
 * }
 * ```
 */
export function useVentureFilters(initialFilters = {}) {
    const [filters, setFilters] = useState(initialFilters);
    /**
     * Set the status filter
     * Requirement 11.3: Filter by status
     */
    const setStatusFilter = useCallback((status) => {
        setFilters((prev) => ({
            ...prev,
            status,
        }));
    }, []);
    /**
     * Set the type (category) filter
     * Requirement 9.3, 12.3: Filter by category
     */
    const setTypeFilter = useCallback((type) => {
        setFilters((prev) => ({
            ...prev,
            type,
        }));
    }, []);
    /**
     * Clear all filters
     */
    const clearFilters = useCallback(() => {
        setFilters({});
    }, []);
    /**
     * Check if any filters are active
     */
    const hasActiveFilters = useMemo(() => {
        return filters.status !== undefined || filters.type !== undefined;
    }, [filters]);
    return {
        filters,
        setStatusFilter,
        setTypeFilter,
        clearFilters,
        hasActiveFilters,
    };
}
/**
 * Hook for managing venture filters with URL synchronization
 * Syncs filter state with URL query parameters for shareable filtered views
 *
 * @param searchParams - URLSearchParams object from router
 * @param setSearchParams - Function to update URL search params
 * @returns Filter state and actions
 *
 * @example
 * ```tsx
 * import { useSearchParams } from 'react-router-dom';
 *
 * function VenturesPage() {
 *   const [searchParams, setSearchParams] = useSearchParams();
 *   const {
 *     filters,
 *     setStatusFilter,
 *     setTypeFilter,
 *     clearFilters
 *   } = useVentureFiltersWithUrl(searchParams, setSearchParams);
 *
 *   const { ventures } = usePublicVentures(filters);
 *
 *   return (
 *     <div>
 *       <FilterBar
 *         onStatusChange={setStatusFilter}
 *         onTypeChange={setTypeFilter}
 *         onClear={clearFilters}
 *       />
 *       <VentureList ventures={ventures} />
 *     </div>
 *   );
 * }
 * ```
 */
export function useVentureFiltersWithUrl(searchParams, setSearchParams) {
    // Parse filters from URL
    const filters = useMemo(() => {
        const status = searchParams.get("status");
        const type = searchParams.get("type");
        return {
            status: status || undefined,
            type: type || undefined,
        };
    }, [searchParams]);
    /**
     * Set the status filter and update URL
     */
    const setStatusFilter = useCallback((status) => {
        const newParams = new URLSearchParams(searchParams);
        if (status) {
            newParams.set("status", status);
        }
        else {
            newParams.delete("status");
        }
        setSearchParams(newParams);
    }, [searchParams, setSearchParams]);
    /**
     * Set the type filter and update URL
     */
    const setTypeFilter = useCallback((type) => {
        const newParams = new URLSearchParams(searchParams);
        if (type) {
            newParams.set("type", type);
        }
        else {
            newParams.delete("type");
        }
        setSearchParams(newParams);
    }, [searchParams, setSearchParams]);
    /**
     * Clear all filters and update URL
     */
    const clearFilters = useCallback(() => {
        const newParams = new URLSearchParams(searchParams);
        newParams.delete("status");
        newParams.delete("type");
        setSearchParams(newParams);
    }, [searchParams, setSearchParams]);
    /**
     * Check if any filters are active
     */
    const hasActiveFilters = useMemo(() => {
        return filters.status !== undefined || filters.type !== undefined;
    }, [filters]);
    return {
        filters,
        setStatusFilter,
        setTypeFilter,
        clearFilters,
        hasActiveFilters,
    };
}
